import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import DOMPurify from "dompurify";
import { fetchSection, recordAck } from "../lib/api";
import { ArrowLeft, Printer } from "lucide-react";
import { renderMarkdown, extractHeadings } from "../lib/markdown";
import SectionRail from "../components/SectionRail";
import { getAcks, setAck, clearAcks, getClientId } from "../lib/storage";

function formatReviewedDate(iso) {
    try {
        return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    } catch { return ""; }
}

export default function Section() {
    const { id } = useParams();
    const [section, setSection] = useState(null);
    const [error, setError] = useState(null);
    const [searchParams] = useSearchParams();
    const highlight = searchParams.get("highlight") || "";

    // Acknowledgements lifted here so we can inject "Last reviewed" badges into rendered DOM
    const [acks, setAcks] = useState({});

    useEffect(() => {
        setSection(null);
        setError(null);
        setAcks(getAcks()[id] || {});
        fetchSection(id)
            .then(setSection)
            .catch((e) => setError(e?.response?.data?.detail || "Section not found"));
        window.scrollTo(0, 0);
    }, [id]);

    // Hash scroll after content renders
    useEffect(() => {
        if (!section) return;
        const hash = window.location.hash.slice(1);
        if (!hash) return;
        const t = setTimeout(() => {
            const el = document.getElementById(hash);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
        return () => clearTimeout(t);
    }, [section]);

    // Inject "Last reviewed" badges after each ticked heading
    useEffect(() => {
        if (!section) return;
        // Wipe previous badges first
        document.querySelectorAll(".last-reviewed-badge").forEach(el => el.remove());
        Object.entries(acks).forEach(([slug, ts]) => {
            const heading = document.getElementById(slug);
            if (!heading) return;
            const badge = document.createElement("div");
            badge.className = "last-reviewed-badge";
            badge.setAttribute("data-testid", `last-reviewed-${slug}`);
            badge.innerHTML = `<span class="dot"></span>Reviewed ${formatReviewedDate(ts)}`;
            heading.insertAdjacentElement("afterend", badge);
        });
        return () => {
            document.querySelectorAll(".last-reviewed-badge").forEach(el => el.remove());
        };
    }, [acks, section]);

    const toggleAck = (slug) => {
        const next = !acks[slug];
        const updated = { ...acks };
        if (next) updated[slug] = new Date().toISOString();
        else delete updated[slug];
        setAcks(updated);
        setAck(id, slug, next);
        // best-effort server sync
        recordAck({ client_id: getClientId(), section_id: id, slug, value: next });
    };

    const resetAcks = () => {
        if (!window.confirm("Reset all acknowledgements for this section?")) return;
        // Untick each on the server too
        Object.keys(acks).forEach(slug => {
            recordAck({ client_id: getClientId(), section_id: id, slug, value: false });
        });
        clearAcks(id);
        setAcks({});
    };

    if (error) {
        return (
            <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
                <div className="text-gray-500 mb-4">{error}</div>
                <Link to="/" className="text-[#2C7A85] underline">← Back to handbook</Link>
            </div>
        );
    }

    if (!section) {
        return (
            <div className="max-w-3xl mx-auto px-6 lg:px-12 py-16 text-gray-500" data-testid="section-loading">Loading…</div>
        );
    }

    const headings = extractHeadings(section.content);
    const rawHtml = renderMarkdown(section.content, highlight);
    const safeHtml = DOMPurify.sanitize(rawHtml, {
        ADD_ATTR: ["target", "rel", "loading", "id"],
    });

    return (
        <article className="fade-up" data-testid={`section-${section.id}`}>
            <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 lg:py-14 flex gap-8">
                <div className="max-w-3xl flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-6 print:hidden">
                        <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#2C7A85]" data-testid="back-to-home">
                            <ArrowLeft className="h-4 w-4" /> Back
                        </Link>
                        <button
                            onClick={() => window.print()}
                            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-[#2C7A85]"
                            data-testid="print-section"
                        >
                            <Printer className="h-4 w-4" /> Print
                        </button>
                    </div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-[#E57373] font-semibold mb-3">{section.category}</div>
                    <div className="prose-handbook" dangerouslySetInnerHTML={{ __html: safeHtml }} />
                </div>

                <SectionRail
                    sectionId={section.id}
                    sectionTitle={section.title}
                    headings={headings}
                    acks={acks}
                    onToggle={toggleAck}
                    onReset={resetAcks}
                />
            </div>
        </article>
    );
}
